/**
 * Cosa comprende un posto, e cosa toglie a una persona (30/10/2026).
 *
 * Nata dai posti di Fortezza: la mattina del 30/10/2026 l'amministratrice ha dato un posto a sedici persone, e Paola
 * Fadda (posto Base) non ha più visto l'Analisi, Andrea Moi (posto Pro) il radar degli appalti. Il gestionale toglieva
 * le funzioni giuste, ma nessuno lo poteva sapere: né il riquadro del posto prima di salvare, né Yuma dopo. Da qui UNA
 * regola, pura, letta dal riquadro del posto e dai comandi di Yuma.
 *
 * - Le funzioni di un posto della SCALA sono l'unione dei piani fino al suo (gratuito ⊂ Base ⊂ Pro ⊂ Business): la
 *   stessa regola con cui il server scrive `app_users.seat_capabilities` (`computeSeatCapabilities` in
 *   `functions/src/_shared/capabilities/tierCapabilities.ts`).
 * - Un posto FUORI DALLA SCALA (02/11/2026: Presenze, per chi timbra soltanto) ha solo le sue funzioni, non comprende
 *   i piani sotto, e decide TUTTO (`seat_esclusivo`): anche le aggiunte e i regali all'azienda valgono solo se il posto
 *   li comprende.
 * - Cosa toglie il posto lo decide `checkCapability` (`shared/capabilities/capabilityService.ts`), la regola di menu,
 *   bottoni e server: una funzione dell'azienda che il posto non comprende risponde `source: 'seat'`. Per i posti della
 *   scala le aggiunte e i regali all'azienda valgono per tutti, quindi non si tolgono mai.
 */
import { checkCapability, type TenantCapabilitySnapshot } from '../capabilities/capabilityService';

/** I piani in ordine: ognuno comprende quelli prima. */
export const PIANI_IN_ORDINE = ['free', 'base', 'pro', 'business'] as const;
export type PianoDelListino = (typeof PIANI_IN_ORDINE)[number];
/** I posti della scala: sono anche piani dell'azienda, e ognuno comprende quelli sotto. */
export const POSTI_DELLA_SCALA = ['base', 'pro', 'business'] as const;
export type PostoDellaScala = (typeof POSTI_DELLA_SCALA)[number];
/**
 * I posti che si assegnano a una persona (il gratuito non è un posto). In fondo quelli FUORI DALLA SCALA (02/11/2026):
 * `presenze`, chi timbra soltanto. Non sono piani dell'azienda e non comprendono niente oltre le loro funzioni.
 */
export const POSTI = [...POSTI_DELLA_SCALA, 'presenze'] as const;
export type PianoDelPosto = (typeof POSTI)[number];

/** Le funzioni che ogni piano elenca da sé (`tier_definitions/{piano}.included_capabilities`). */
export type FunzioniPerPiano = ReadonlyMap<string, readonly string[]>;

export function ePianoDelPosto(v: unknown): v is PianoDelPosto {
  return typeof v === 'string' && (POSTI as readonly string[]).includes(v);
}

/** Un posto della scala, cioè anche un piano dell'azienda (Base, Pro, Business). */
export function ePostoDellaScala(v: unknown): v is PostoDellaScala {
  return typeof v === 'string' && (POSTI_DELLA_SCALA as readonly string[]).includes(v);
}

/**
 * Un posto che si assegna ma non è un piano (02/11/2026): ha solo le sue funzioni, decide tutto (`seat_esclusivo`),
 * non diventa mai il piano dell'azienda e non si compra da solo.
 */
export function ePostoFuoriDallaScala(v: unknown): v is Exclude<PianoDelPosto, PostoDellaScala> {
  return ePianoDelPosto(v) && !ePostoDellaScala(v);
}

/** Le funzioni di un posto: per la scala il suo piano e tutti quelli sotto, fuori dalla scala solo le sue. */
export function funzioniDelPosto(perPiano: FunzioniPerPiano, posto: PianoDelPosto): string[] {
  if (ePostoFuoriDallaScala(posto)) return [...new Set(perPiano.get(posto) ?? [])].sort();
  return funzioniDelPiano(perPiano, posto as PostoDellaScala);
}

/** Le funzioni di un piano della scala: quelle del suo piano e di tutti i piani sotto. */
export function funzioniDelPiano(perPiano: FunzioniPerPiano, piano: PianoDelListino): string[] {
  const fino = PIANI_IN_ORDINE.indexOf(piano);
  const tutte = new Set<string>();
  PIANI_IN_ORDINE.slice(0, fino + 1).forEach((p) => (perPiano.get(p) ?? []).forEach((c) => tutte.add(c)));
  return [...tutte].sort();
}

/** Il piano più basso che comprende una funzione; null = nessun piano la elenca. */
export function pianoCheLaComprende(perPiano: FunzioniPerPiano, capability: string): PianoDelListino | null {
  return PIANI_IN_ORDINE.find((p) => (perPiano.get(p) ?? []).includes(capability)) ?? null;
}

/**
 * Le funzioni dell'azienda che una persona NON ha a causa del suo posto. `funzioniDelPosto` null o assente = posto
 * non assegnato: eredita il piano dell'azienda e non perde niente.
 */
export function funzioniTolteDalPosto(
  azienda: TenantCapabilitySnapshot | null | undefined,
  funzioniDelPosto: readonly string[] | null | undefined,
  esclusivo: boolean = false,
): string[] {
  if (funzioniDelPosto == null) return [];
  const m = azienda?.modules;
  // Un posto della scala toglie solo funzioni del piano; uno fuori dalla scala anche aggiunte e regali (02/11/2026).
  const candidate = esclusivo
    ? [...new Set([...(m?.base ?? []), ...(m?.addon ?? []), ...(m?.override ?? [])] as string[])]
    : ((m?.base as readonly string[] | undefined) ?? []);
  return candidate.filter((c) => checkCapability(azienda, c, { seat_capabilities: funzioniDelPosto, seat_esclusivo: esclusivo }).source === 'seat');
}

/** Il confronto fra il posto di adesso e un altro: cosa la persona perde e cosa riacquista. */
export function cambioDiPosto(
  azienda: TenantCapabilitySnapshot | null | undefined,
  perPiano: FunzioniPerPiano,
  adesso: readonly string[] | null | undefined,
  dopo: PianoDelPosto | null,
  adessoEsclusivo: boolean = false,
): { perde: string[]; riacquista: string[] } {
  const tolteAdesso = new Set(funzioniTolteDalPosto(azienda, adesso, adessoEsclusivo));
  const tolteDopo =
    dopo === null ? [] : funzioniTolteDalPosto(azienda, funzioniDelPosto(perPiano, dopo), ePostoFuoriDallaScala(dopo));
  const dopoSet = new Set(tolteDopo);
  return {
    perde: tolteDopo.filter((c) => !tolteAdesso.has(c)),
    riacquista: [...tolteAdesso].filter((c) => !dopoSet.has(c)),
  };
}
